import Vue from 'vue'
import { getUserId } from '@/utils/data-check'
import { alert as $alert } from '@/utils/alert'
import orderApis from '@/apis/order'
import { api } from '@/apis/api'
import {
	calOrderAutomaticDiscountPrice,
	findNextOrderAutomaticDiscount,
	findUseOrderAutomaticDiscount,
	getCartTotal,
} from '@/utils/order'
import { getTotalName } from './helper'
import storeManager from '@/utils/storeManager'
import { compareCartWithLatest } from '@/utils/compareCart'

const createDefaultAutomaticDiscountInfo = () => ({
	orderAutomaticDiscountPrice: 0,
	useOrderAutomaticDiscount: null,
	nextOrderAutomaticDiscount: null,
})

export default {
	namespaced: true,
	state: {
		authToken: '',
		wsToken: '',
		userName: '',
		userId: null,
		// 多店家信息存储: { "qrcodehash": { storeId: xxx, storeName: "xxx" } }
		storeInfo: {},
		isMemoryAccount: false,
		account: '',
		cart: {
			guest: {},
		},
		userPhone: '',
		guestId: null,

		// 遊客結帳資訊暫存，隨 user 模組持久化到 localStorage 供下次帶入
		guestUserPreferences: {
			uniformNumber: '',
			phoneCarrier: '',
			donationNumber: '',
			address: '',
		},
		// 是否顯示WhatsEat優惠卷
		showWhatsEatCoupon: true,
		operationHasFailed: false,
		// 多店家訂單折扣資訊: { [storeId]: { orderAutomaticDiscountPrice, useOrderAutomaticDiscount, nextOrderAutomaticDiscount } }
		automaticDiscountInfo: {},
		tableNumber: null,
		pickUpMethod: null,
		pickUpTimeType: null,
		pickUpDate: null,
		pickUpTime: null,
		deliveryCheckStatus: null,
	},
	getters: {
		/**
		 * 取得取餐時間的顯示文字
		 * @returns {string} 格式化的取餐時間文字，例如「今天 12/17(二) 14:30」
		 */
		pickUpTimeText: state => {
			if (!state.pickUpDate || !state.pickUpTime) {
				return ''
			}
			const offsetMap = ['今天', '明天', '後天']
			const pickUpDate = new Date(state.pickUpDate)
			const weekdayMap = ['日', '一', '二', '三', '四', '五', '六']
			const dayOfWeek = weekdayMap[pickUpDate.getDay()]
			const today = new Date()
			today.setHours(0, 0, 0, 0)
			const targetDate = new Date(pickUpDate)
			targetDate.setHours(0, 0, 0, 0)
			const offset = Math.round((targetDate - today) / 86400000)
			const month = pickUpDate.getMonth() + 1
			const day = pickUpDate.getDate()
			const dateText =
				offset >= 0 && offset <= 2
					? `${offsetMap[offset]} ${month}/${day}`
					: `${month}/${day}`
			const [hours, minutes] = state.pickUpTime.split(':')
			const timeText = `${hours}:${minutes}`
			return `${dateText}(${dayOfWeek}) ${timeText}`
		},
		/**
		 * 取得取餐方式的顯示文字
		 * @returns {string} 根據取餐方式返回對應的顯示文字
		 */
		pickUpDisplayText: (state, getters) => {
			switch (state.pickUpMethod) {
				case 'DINE_IN': {
					const parts = ['內用']
					if (state.tableNumber) parts.push(`桌號 ${state.tableNumber}`)
					return parts.join('，')
				}
				case 'SELF': {
					const parts = ['外帶自取']
					if (state.pickUpTimeType === 'NOW') {
						parts.push('馬上取餐')
					} else if (getters.pickUpTimeText) {
						parts.push('預約取餐', getters.pickUpTimeText)
					}
					return parts.join('，')
				}
				case 'DELIVERY': {
					const parts = ['外送']
					if (getters.pickUpTimeText) parts.push(getters.pickUpTimeText)
					return parts.join('，')
				}
				default:
					return '選擇取餐方式和時間'
			}
		},
		// 依據 qrCodeHash 取得當前購物車
		currCart: (state, getters) => qrCodeHash => {
			const storeId = state.storeInfo[qrCodeHash]?.storeId || null
			if (!storeId) return null
			const userId = getUserId(state.userId)
			return state.cart[userId]?.[storeId] || null
		},
		// 依據 qrCodeHash 取得購物車總價
		cartTotal: (state, getters) => qrCodeHash => {
			const currCart = getters.currCart(qrCodeHash)
			return getCartTotal(currCart)
		},
		// 依據 qrCodeHash 取得商品數量
		productLength: (state, getters) => qrCodeHash => {
			const currCart = getters.currCart(qrCodeHash)
			return currCart ? currCart.reduce((p, e) => (e.count || 0) + p, 0) : 0
		},
		// 依據 qrCodeHash 檢查購物車是否有免費商品
		currCartHasFreeItem: (state, getters) => qrCodeHash => {
			const currCart = getters.currCart(qrCodeHash)
			return (
				currCart && currCart.length > 0 && currCart.some(item => item.freeItem)
			)
		},
		automaticDiscountInfoByStore: state => storeId => {
			if (!storeId) return createDefaultAutomaticDiscountInfo()
			return (
				state.automaticDiscountInfo[storeId] ||
				createDefaultAutomaticDiscountInfo()
			)
		},
		orderAutomaticDiscountPriceByStore: (state, getters) => storeId => {
			const info = getters.automaticDiscountInfoByStore(storeId)
			return info.orderAutomaticDiscountPrice || 0
		},
		useOrderAutomaticDiscountByStore: (state, getters) => storeId => {
			const info = getters.automaticDiscountInfoByStore(storeId)
			return info.useOrderAutomaticDiscount || null
		},
		nextOrderAutomaticDiscountByStore: (state, getters) => storeId => {
			const info = getters.automaticDiscountInfoByStore(storeId)
			return info.nextOrderAutomaticDiscount || null
		},
		pickUpTime: (state, getters) => () => {
			if (state.pickUpDate && state.pickUpTime) {
				return { date: state.pickUpDate, time: state.pickUpTime }
			}
			return null
		},
	},
	mutations: {
		SET_AUTH_TOKEN(state, value) {
			state.authToken = value
		},
		SET_WS_TOKEN(state, value) {
			state.wsToken = value
		},
		CLEAR_AUTH_TOKENS(state) {
			state.authToken = ''
			state.wsToken = ''
			state.userId = null
			state.guestId = null
		},
		SET_STORE(state, payload = {}) {
			const { qrCodeHash, storeId, storeName } = payload
			if (!qrCodeHash) {
				const currentHash = storeManager.qrCodeHash
				if (currentHash && state.storeInfo[currentHash]) {
					Vue.delete(state.storeInfo, currentHash)
				}

				const currentStoreId = storeManager.storeId
				if (currentStoreId && state.automaticDiscountInfo[currentStoreId]) {
					Vue.delete(state.automaticDiscountInfo, currentStoreId)
				}

				storeManager.reset()
				return
			}

			Vue.set(state.storeInfo, qrCodeHash, { storeId, storeName })
			if (storeId && !state.automaticDiscountInfo[storeId]) {
				Vue.set(
					state.automaticDiscountInfo,
					storeId,
					createDefaultAutomaticDiscountInfo(),
				)
			}
			storeManager.syncStoreInfo(qrCodeHash, state.storeInfo)
		},
		REMOVE_STORE(state, qrCodeHash) {
			if (!qrCodeHash) return
			const storeInfo = state.storeInfo[qrCodeHash]
			const storeId = storeInfo?.storeId
			if (storeInfo) {
				Vue.delete(state.storeInfo, qrCodeHash)
			}
			if (storeId && state.automaticDiscountInfo[storeId]) {
				Vue.delete(state.automaticDiscountInfo, storeId)
			}
			if (storeManager.qrCodeHash === qrCodeHash) {
				storeManager.reset()
			}
		},
		SET_PICKUP_METHOD(state, value) {
			state.pickUpMethod = value
		},
		SET_TABLE_NUMBER(state, value) {
			state.tableNumber = value
		},
		SET_USER_NAME(state, value) {
			state.userName = value
		},
		SET_USER_PHONE(state, value) {
			state.userPhone = value
		},
		SET_PICKUP_TIME_TYPE(state, value) {
			state.pickUpTimeType = value
		},
		SET_PICKUP_DATE(state, value) {
			state.pickUpDate = value
		},
		SET_PICKUP_TIME(state, value) {
			state.pickUpTime = value
		},
		SET_DELIVERY_CHECK_STATUS(state, value) {
			state.deliveryCheckStatus = value
		},
		SET_GUEST_USER_PREFERENCES(state, value) {
			if (!value) return

			// 空字串不會覆蓋既有值，清空須改用 CLEAR_GUEST_USER_PREFERENCES
			state.guestUserPreferences = {
				uniformNumber:
					value.uniformNumber || state.guestUserPreferences.uniformNumber || '',
				phoneCarrier:
					value.phoneCarrier || state.guestUserPreferences.phoneCarrier || '',
				donationNumber:
					value.donationNumber ||
					state.guestUserPreferences.donationNumber ||
					'',
				address: value.address || state.guestUserPreferences.address || '',
			}
		},
		CLEAR_GUEST_USER_PREFERENCES(state) {
			// 登出時清空遊客偏好設定
			state.guestUserPreferences = {
				uniformNumber: '',
				phoneCarrier: '',
				donationNumber: '',
				address: '',
			}
		},
		/**
		 * 更新單一使用者偏好設定欄位
		 * @param state
		 * @param {Object} payload - { key: string, value: any }
		 */
		UPDATE_GUEST_USER_PREFERENCE(state, { key, value }) {
			if (
				Object.prototype.hasOwnProperty.call(state.guestUserPreferences, key)
			) {
				state.guestUserPreferences[key] = value
			}
		},
		SET_USER_ID(state, value) {
			state.userId = value
		},
		SET_GUEST_ID(state, value) {
			state.guestId = value
		},
		SET_ACCOUNT(state, value) {
			state.account = value
		},
		SET_IS_MEMORY_ACCOUNT(state, value) {
			state.isMemoryAccount = value
		},
		SET_SHOW_WHATS_EAT_COUPON(state, val) {
			state.showWhatsEatCoupon = val
		},
		SET_OPERATION_HAS_FAILED(state, val) {
			state.operationHasFailed = val
		},
		CHECK_CART_AND_SET($) {
			const { cart, userId } = $
			const storeId = storeManager.storeId
			const newUserId = getUserId(userId)
			if (storeId) {
				if (!cart[newUserId]) {
					Vue.set(cart, newUserId, {})
				}
				if (!cart[newUserId][storeId]) {
					Vue.set(cart[newUserId], storeId, [])
				}
			}
		},
		SET_CART_ITEM($, val) {
			this.commit('user/CHECK_CART_AND_SET')
			const storeId = storeManager.storeId
			if (storeId) {
				$.cart[getUserId($.userId)][storeId] = val
			}
		},
		SET_GUEST_CART($, val) {
			this.commit('user/CHECK_CART_AND_SET')
			const storeId = storeManager.storeId
			if (storeId) {
				$.cart['guest'][storeId] = val
			}
		},
		ADD_CART_ITEM($, val) {
			this.commit('user/CHECK_CART_AND_SET')
			const storeId = storeManager.storeId
			if (storeId) {
				$.cart[getUserId($.userId)][storeId].push(val)
			}
		},
		REMOVE_CART_ITEM($, { index }) {
			this.commit('user/CHECK_CART_AND_SET')
			const storeId = storeManager.storeId
			if (storeId) {
				$.cart[getUserId($.userId)][storeId].splice(index, 1)
			}
		},
		UPDATE_CART_ITEM($, { data, index }) {
			this.commit('user/CHECK_CART_AND_SET')
			const { cart, userId } = $
			const storeId = storeManager.storeId
			if (storeId) {
				const storeCart = cart[getUserId(userId)][storeId]
				cart[getUserId(userId)][storeId] = [
					...storeCart.slice(0, index),
					data,
					...storeCart.slice(index + 1, storeCart.length),
				]
			}
		},
		SET_CART($, val) {
			$.cart = val
		},
		// 清除購物車的衝突標記（紅框）。conflicts 是「某次 compareCart 當下的比對結果」，
		// 但 user 模組整份持久化到 localStorage，重開頁面會把過期的紅框一起還原 ——
		// 故啟動時清一次。清掉不影響安全性：送單前必經 compareCart，屆時會重新標記。
		CLEAR_CART_CONFLICTS($) {
			this.commit('user/CHECK_CART_AND_SET')
			const storeId = storeManager.storeId
			if (!storeId) return

			const gUserId = getUserId($.userId)
			const storeCart = $.cart[gUserId][storeId] || []
			if (!storeCart.some(item => item.conflicts)) return

			$.cart[gUserId][storeId] = storeCart.map(item => {
				if (!item.conflicts) return item
				const rest = { ...item }
				delete rest.conflicts
				return rest
			})
		},
		CLEAR_CART($) {
			const storeId = storeManager.storeId
			if (storeId) {
				$.cart[getUserId($.userId)][storeId] = []
				this.commit('user/CHECK_CART_AND_SET')
			}
		},
		CLEAR_PICKUP_SELECTIONS(state) {
			state.tableNumber = null
			state.pickUpTimeType = null
			state.pickUpDate = null
			state.pickUpTime = null
		},
		RESET_PICKUPMETHOD(state) {
			state.pickUpMethod = null
		},
		// 登入 A+B 及 Detail 購物車新增
		LOGIN_AMOUNT_CART(
			$,
			{ cartItems, callback, isResetCart = true, editIndex = null } = {},
		) {
			const { cart, userId } = $
			const storeId = storeManager.storeId
			if (!storeId) return
			const guestStore = cartItems ? cartItems : cart.guest[storeId]
			if (guestStore.length) {
				const userCart = cart[getUserId(userId)]
				const storeCart = userCart ? userCart[storeId] : []
				const storeCartLen = storeCart.length
				if (storeCartLen === 0 && guestStore.length === 1) {
					editIndex == null && this.commit('user/ADD_CART_ITEM', guestStore[0])
				} else {
					guestStore.forEach(guItem => {
						if (storeCartLen === 0) {
							editIndex == null && this.commit('user/ADD_CART_ITEM', guItem)
						} else {
							let isAddItem = false
							for (let scI = 0; scI < storeCartLen; scI++) {
								const scItem = storeCart[scI]
								const newItem = {
									...scItem,
									count: scItem.count + guItem.count,
								}
								// 同品項合併條件
								if (
									scItem.itemId === guItem.itemId &&
									scItem.itemRemark === guItem.itemRemark &&
									scItem.freeItem === guItem.freeItem
								) {
									const guName = getTotalName(guItem)
									const scName = getTotalName(scItem)
									if (guName === scName) {
										this.commit('user/UPDATE_CART_ITEM', {
											data: editIndex !== scI ? newItem : cartItems[0],
											index: scI,
										})
										if (editIndex != null && editIndex !== scI) {
											this.commit('user/REMOVE_CART_ITEM', { index: editIndex })
										}
										isAddItem = false
										break
									} else {
										if (scI === storeCart.length - 1) {
											isAddItem = true
										}
									}
								} else {
									isAddItem = true
								}
							}

							if (editIndex == null) {
								if (isAddItem) {
									this.commit('user/ADD_CART_ITEM', guItem)
								}
							} else {
								if (isAddItem) {
									this.commit('user/UPDATE_CART_ITEM', {
										data: cartItems[0],
										index: editIndex,
									})
								}
							}
						}
					})
				}
				callback && callback()
			}
			isResetCart && this.commit('user/SET_GUEST_CART', [])
		},
		// 變更狀態 : 套用訂單自動折扣、訂單自動折扣金額，目標訂單自動折扣
		SET_CURRENT_ORDER_AUTOMATIC_DISCOUNT(state, { cartTotal, list }) {
			const storeId = storeManager.storeId
			if (!storeId) return

			let info =
				state.automaticDiscountInfo[storeId] ||
				createDefaultAutomaticDiscountInfo()

			if (list.length > 0) {
				const use = findUseOrderAutomaticDiscount(cartTotal, list)
				const next = findNextOrderAutomaticDiscount(cartTotal, list)
				const orderAutomaticDiscountPrice = use
					? calOrderAutomaticDiscountPrice(cartTotal, use)
					: 0

				info = {
					orderAutomaticDiscountPrice,
					useOrderAutomaticDiscount: use,
					nextOrderAutomaticDiscount: next,
				}
			} else {
				info = createDefaultAutomaticDiscountInfo()
			}

			Vue.set(state.automaticDiscountInfo, storeId, info)
		},

		// 清除訂單折扣相關資訊
		CLEAR_CURRENT_ORDER_AUTOMATIC_DISCOUNT(state) {
			const storeId = storeManager.storeId
			if (!storeId) return
			Vue.set(
				state.automaticDiscountInfo,
				storeId,
				createDefaultAutomaticDiscountInfo(),
			)
		},
	},
	actions: {
		/**
		 * 處理取餐方式確認
		 * @param {Object} pickUpDraft - 取餐方式草稿資料
		 */
		confirmPickupMethod({ commit }, pickUpDraft) {
			const {
				pickUpMethod,
				tableNumber,
				userName,
				phone: userPhone,
				pickUpTimeType,
				pickUpDate,
				pickUpTime,
				address: userAddress,
			} = pickUpDraft

			commit('CLEAR_PICKUP_SELECTIONS')

			commit('SET_PICKUP_METHOD', pickUpMethod)
			commit('SET_USER_NAME', userName)
			commit('SET_USER_PHONE', userPhone)

			switch (pickUpMethod) {
				case 'DINE_IN':
					commit('SET_TABLE_NUMBER', tableNumber)
					break
				case 'SELF':
					commit('SET_PICKUP_TIME_TYPE', pickUpTimeType)
					commit('SET_PICKUP_DATE', pickUpDate)
					commit('SET_PICKUP_TIME', pickUpTime)
					break
				case 'DELIVERY':
					commit('UPDATE_GUEST_USER_PREFERENCE', {
						key: 'address',
						value: userAddress,
					})
					commit('SET_PICKUP_DATE', pickUpDate)
					commit('SET_PICKUP_TIME', pickUpTime)
					break
			}
		},
		/**
		 * 清除 Kiosk 登入狀態
		 * 執行登出 API、清除認證 tokens、重置 kiosk 狀態、清除使用者資訊與購物車
		 * @param {Function} commit - Vuex commit 函數
		 * @param {Object} rootState - Vuex 根狀態
		 * @param {Object} options - 選項
		 * @param {boolean} [options.callLogoutApi=true] - 是否調用登出 API
		 * @param {boolean} [options.clearCart=false] - 是否清空購物車
		 * @returns {Promise<void>}
		 */
		async clearKioskSession({ commit, rootState }, options = {}) {
			const { callLogoutApi = true, clearCart = false } = options

			// 1. 調用登出 API
			if (callLogoutApi) {
				await api.user.logout().catch(err => {
					if (err.status !== 401) {
						console.warn('[user/clearKioskSession] logout failed:', err)
					}
				})
			}

			// 2. 清除認證 tokens
			commit('CLEAR_AUTH_TOKENS')

			// 3. 重置 kiosk 狀態
			commit('kiosk/SET_IS', false, { root: true })

			// 4. 清除使用者資訊
			commit('SET_USER_NAME', '')
			commit('SET_USER_PHONE', '')
			commit('SET_GUEST_ID', null)
			commit('CLEAR_GUEST_USER_PREFERENCES')

			// 5. 清除 kiosk 訂單備註
			rootState.kioskOrderRemark = ''

			// 6. 可選：清空購物車
			if (clearCart) {
				commit('CLEAR_CART')
			}
		},

		// 購物車數據比對：異動品項不移除，改掛 conflicts 標記（UI 紅框），並把最新商品資料 patch 回菜單
		async compareCart({ commit, state }) {
			this.commit('user/CHECK_CART_AND_SET')
			const statusEnum = {
				EQUALS: 0, // 比對結果相仿
				NODATA: 0, // 購物車無資料
				NOT_EQUALS: 1, // 資料有異動
				ERROR: 2, // 語法或api錯誤
			}
			const { userId, cart } = state
			const storeId = storeManager.storeId
			const gUserId = getUserId(userId)
			const storeCart = cart[gUserId][storeId]
			const productIdList = [
				...new Set(storeCart.map(cartItem => cartItem.itemId)),
			]
			if (productIdList.length === 0) {
				return { message: '購物車無資料', status: statusEnum.NODATA }
			}

			try {
				const res = (
					await orderApis.getItems({
						itemIdList: productIdList,
						storeId: storeId,
					})
				).data

				const latestItemsById = res.reduce((lastVal, item) => {
					lastVal[item.itemId] = item
					return lastVal
				}, {})

				const { results, conflictItemNames, hasConflicts } =
					compareCartWithLatest(storeCart, latestItemsById)

				if (hasConflicts) {
					// 衝突可能來自口味／套餐等巢狀資料異動，讓本輪第一次
					// 重新選購補抓完整菜單；後續衝突品項共用同一快照。
					commit('store/BUMP_CART_CONFLICT_REVISION', null, { root: true })
				}

				// 最新商品頂層資料（品名/價格/售完）同步回菜單快取
				commit('store/PATCH_MENU_ITEMS', res, { root: true })

				commit('SET_CART', {
					...cart,
					[gUserId]: {
						...cart[gUserId],
						[storeId]: results.map(({ item, conflicts }) => {
							// 衝突標記掛在 cart item 上（隨 user 模組持久化，欄位向後相容），無衝突者清除殘留標記
							const rest = { ...item }
							delete rest.conflicts
							return conflicts.length > 0 ? { ...rest, conflicts } : rest
						}),
					},
				})

				// 只回計數不列品名：品項一多會串成一長串引號頓號、撐爆彈窗且難讀，
				// 而 conflictItemNames 對同名不同客製的品項已去重（看了也分不出是哪一份），
				// 使用者回購物車看紅框才是完整資訊。後半句引導語由 UI 層補上，兩段合計
				// 要能在窄螢幕彈窗內排成一行；主詞用「餐點內容」而非「店家」，避免讀成
				// 店家動了顧客購物車裡的東西
				return {
					message: `有 ${conflictItemNames.length} 項餐點的內容已更新`,
					status: hasConflicts ? statusEnum.NOT_EQUALS : statusEnum.EQUALS,
				}
			} catch (err) {
				// getItems 失敗（網路 / 伺服器異常）→ 整車清空
				$devLog('error', err)
				$alert('網頁操作逾期，請刷新頁面後重新進行點餐', 'danger')
				commit('SET_CART_ITEM', [])
				commit('CLEAR_CURRENT_ORDER_AUTOMATIC_DISCOUNT')
				commit('SET_OPERATION_HAS_FAILED', true)

				return Promise.reject({
					message: '網頁操作逾期，請刷新頁面後重新進行點餐',
					status: statusEnum.ERROR,
				})
			}
		},
	},
}
